import React, { useState, useMemo, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  useTheme,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { LoadingIndicator } from "./LoadingIndicator";
import { ErrorHandler } from "./ErrorHandler";
import { styled } from "@mui/material/styles";
import moment from "moment";
import Market from "./Market";
import BookmakersFilter from "./BookmakersFilter";
import useMarketGroupings from "../hooks/useMarketGroupings";

const StyledTab = styled(Tab)(({ theme, selected }) => ({
  transition: "all 0.2s ease",
  borderRadius: "100px",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: selected ? theme.palette.primary.main : theme.palette.divider,
  fontWeight: 600,
  backgroundColor: selected
    ? theme.palette.primary.main
    : theme.palette.background.paper,
  color: selected
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary,
  textTransform: "none",
  "&:hover": {
    backgroundColor: selected
      ? theme.palette.primary.dark
      : theme.palette.action.hover,
  },
  "&.Mui-selected": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textDecoration: "none",
  },
  "&.Mui-selected:hover": {
    backgroundColor: theme.palette.primary.dark,
    borderColor: theme.palette.primary.dark,
  },
  ".MuiTouchRipple-root": {
    display: "none",
  },
  "&.Mui-focusVisible": {
    backgroundColor: theme.palette.primary.main,
  },
}));

const useStyledTabs = () => {
  const theme = useTheme();
  return {
    StyledTabs: styled(Tabs)({
      justifyContent: "flex-start",
      marginTop: "32px",
      ".MuiTabs-indicator": {
        display: "none",
      },
      ".MuiTabs-flexContainer": {
        gap: "8px",
      },
      ".MuiTab-root": {
        margin: "0 !important",
        padding: "0",
      },
    }),
  };
};

const MarketsList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const targetMarket = searchParams.get("market");

  // Extract eventId from the URL hash
  const eventId = location.pathname.split("/").pop();

  const { data, loading, error } = useMarketGroupings(eventId);
  const [selectedMarketGroup, setSelectedMarketGroup] = useState("1");
  const { StyledTabs } = useStyledTabs();
  const [favoriteMarkets, setFavoriteMarkets] = useState(() => {
    const storedFavorites = localStorage.getItem("favoriteMarkets");
    return storedFavorites ? JSON.parse(storedFavorites) : [];
  });

  // Effect to handle market parameter and scrolling
  useEffect(() => {
    if (targetMarket && data?.markets && data?.marketGroupings) {
      // Find the market that matches the target
      const targetMarketData = data.markets.find(
        (market) => market.market === targetMarket
      );

      if (targetMarketData) {
        // Set the correct market group
        setSelectedMarketGroup(targetMarketData.marketGroup.toString());

        // Wait for the DOM to update before scrolling
        setTimeout(() => {
          const marketElement = document.getElementById(
            targetMarketData.marketId
          );
          if (marketElement) {
            marketElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);
      }

      // Clear the market parameter from the URL without navigating
      const newUrl = `${location.pathname}${location.search.replace(
        `?market=${encodeURIComponent(targetMarket)}`,
        ""
      )}`;
      window.history.replaceState({}, "", newUrl);
    }
  }, [targetMarket, data, location]);

  const toggleFavoriteMarket = (marketId) => {
    setFavoriteMarkets((prevFavorites) => {
      const isFavorite = prevFavorites.includes(marketId);
      const updatedFavorites = isFavorite
        ? prevFavorites.filter((fav) => fav !== marketId)
        : [...prevFavorites, marketId];
      localStorage.setItem("favoriteMarkets", JSON.stringify(updatedFavorites));
      return updatedFavorites;
    });
  };

  const marketGroupings = data?.marketGroupings || {};
  const markets =
    data?.markets
      ?.filter(
        (market) =>
          market?.marketCount > 0 ||
          market?.market?.toLowerCase().includes("main")
      )
      .sort((a, b) => {
        const levelDiff = (a.marketLevel || 0) - (b.marketLevel || 0);
        if (levelDiff === 0) {
          return (a.market || "").localeCompare(b.market || "");
        }
        return levelDiff;
      }) || [];
  const handleMarketGroupChange = (event, newValue) => {
    setSelectedMarketGroup(newValue);
  };

  const filteredMarkets = useMemo(() => {
    const favoritedMarkets = markets
      .filter((market) => favoriteMarkets.includes(market.marketId))
      .filter((market) => market.marketGroup === parseInt(selectedMarketGroup));
    const nonFavoriteMarkets = markets
      .filter((market) => !favoriteMarkets.includes(market.marketId))
      .filter((market) => market.marketGroup === parseInt(selectedMarketGroup));

    return [...favoritedMarkets, ...nonFavoriteMarkets].sort((a, b) => a.market.localeCompare(b.market));
  }, [markets, selectedMarketGroup, favoriteMarkets]);

  // Dropdown menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleMenuItemClick = (id) => {
    handleClose();
    navigate(`/event/${id}`);
  };

  return (
    <Box
      sx={{
        margin: { xs: "24px 8px", sm: 3 },
        position: "relative",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <Box sx={{ maxWidth: "1200px", width: "100%" }}>
          <Button
            component={Link}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 1,
              mb: 1,
              p: 0,
              width: "max-content",
              textTransform: "none",
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <ArrowBackIcon sx={{ height: "16px", width: "16px" }} />
            <Typography variant="h6" component="span">
              Back to Events
            </Typography>
          </Button>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h3" sx={{ mb: 1, fontWeight: "bold" }}>
              {data?.event}
              <IconButton onClick={handleClick}>
                <ArrowDropDownIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                {data?.relatedEvents?.map((relatedEvent) => (
                  <MenuItem
                    key={relatedEvent.id}
                    onClick={() => handleMenuItemClick(relatedEvent.id)}
                  >
                    {relatedEvent.name}
                  </MenuItem>
                ))}
              </Menu>
            </Typography>
            <Typography variant="subtitle1">
              {moment(data?.datetime).local().format("MMM Do YYYY, h:mm a")}
            </Typography>
          </Box>

          <StyledTabs
            value={selectedMarketGroup}
            onChange={handleMarketGroupChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2 }}
          >
            {Object.entries(marketGroupings).map(([key, value]) => (
              <StyledTab
                key={key}
                label={value}
                value={key}
                selected={selectedMarketGroup === key}
              />
            ))}
          </StyledTabs>

          <Box sx={{ width: { xs: "100%", sm: "50%" }, mb: 2 }}>
            <BookmakersFilter />
          </Box>
        </Box>

        {loading && <LoadingIndicator />}
        <ErrorHandler error={error} />
        {!loading && !error && (
          <Box
            sx={{
              padding: { xs: "24px 0px" },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            {filteredMarkets.map((market, index) => (
              <Market
                key={market.marketId}
                marketData={market}
                eventId={eventId}
                level={data?.level}
                defaultExpanded={targetMarket === market.market}
                onFavoriteToggle={toggleFavoriteMarket}
                isFavorite={favoriteMarkets.includes(market.marketId)}
                event={data?.event}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MarketsList;
